import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Combines multiple class names or class value arrays into a single string.
 * This function uses `clsx` to merge the inputs and `twMerge` to handle
 * Tailwind CSS class conflicts.
 *
 * @param inputs - An array of class values (strings, objects, or arrays)
 * @returns A string of combined and de-duplicated class names
 *
 * @example
 * cn('foo', 'bar'); // => 'foo bar'
 * cn('foo', { bar: true }); // => 'foo bar'
 * cn({ 'foo-bar': false }); // => ''
 * cn({ 'foo-bar': true }); // => 'foo-bar'
 * cn('foo', { bar: true, 'baz': false }); // => 'foo bar'
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Creates a promise that resolves after a specified duration.
 * This function can be used to introduce delays in async operations.
 *
 * @param ms - The duration to sleep, either as a number (milliseconds) or a string (e.g., '1000ms')
 * @returns A promise that resolves after the specified duration
 *
 * @example
 * await sleep(1000); // Sleep for 1 second
 * await sleep('500ms'); // Sleep for 500 milliseconds
 */
export const sleep = (ms: number | `${number}ms`) => {
  let duration: number;
  if (typeof ms === "string") {
    duration = Number.parseInt(ms);
  } else {
    duration = ms;
  }
  return new Promise((resolve) => setTimeout(resolve, duration));
};

export const calculatePageSize = () => {
  const { innerHeight } = window;

  const subtractions = [
    50, //crumbs
    50, //actions
    100, //footer
  ];

  const rowHeight = 40;

  const availableHeight = innerHeight - subtractions.reduce((acc, value) => acc + value, 0);

  const result = Math.floor(availableHeight / rowHeight);

  return Math.max(10, Math.min(result, 100));
};

export const hexToHslValues = (hex: string): string => {
  // Remove the '#' if present
  hex = hex.replace("#", "");

  // Parse the hex color
  let r: number, g: number, b: number;
  if (hex.length === 6) {
    r = Number.parseInt(hex.substring(0, 2), 16);
    g = Number.parseInt(hex.substring(2, 4), 16);
    b = Number.parseInt(hex.substring(4, 6), 16);
  } else if (hex.length === 3) {
    r = Number.parseInt(hex[0] + hex[0], 16);
    g = Number.parseInt(hex[1] + hex[1], 16);
    b = Number.parseInt(hex[2] + hex[2], 16);
  } else {
    throw new Error("Invalid hex color");
  }

  // Normalize the RGB values to the range [0, 1]
  r /= 255;
  g /= 255;
  b /= 255;

  // Find the minimum and maximum RGB values
  const min = Math.min(r, g, b);
  const max = Math.max(r, g, b);
  const delta = max - min;

  // Calculate lightness
  let l = (max + min) / 2;

  let h = 0;
  let s = 0;

  // If the delta is 0, it's a shade of grey (no saturation)
  if (delta !== 0) {
    // Calculate saturation
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);

    // Calculate hue
    switch (max) {
      case r:
        h = (g - b) / delta + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / delta + 2;
        break;
      case b:
        h = (r - g) / delta + 4;
        break;
    }
    h /= 6;
  }

  // Convert h, s, l to percentage values
  h = Math.round(h * 360);
  s = Math.round(s * 100);
  l = Math.round(l * 100);

  // Return as a string in the format "h, s%, l%"
  return `${h}, ${s}%, ${l}%`;
};

/**
 * Formats location coordinates in the same way as the Java backend
 * String.format("%03d%03d%03d%03d", getRack(), getSection(), getPillar(), getLevel())
 *
 * @param rack - The rack number
 * @param section - The section number
 * @param pillar - The pillar number
 * @param level - The level number
 * @returns A formatted coordinate string with each component padded to 3 digits
 *
 * @example
 * formatLocationCoordinate(1, 2, 3, 4); // => "001002003004"
 * formatLocationCoordinate(123, 456, 789, 0); // => "123456789000"
 */
export function formatLocationCoordinate(rack: number, section: number, pillar: number, level: number): string {
  return [rack, section, pillar, level].map((num) => num.toString().padStart(3, "0")).join("");
}
